import React, { useState } from 'react';
import { BiDislike, BiLike, BiSolidDislike, BiSolidLike } from 'react-icons/bi';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import type { RootState } from '../../../../state/store';
import { useAskVantage } from '../../context/AskVantageProvider';
import { MessageVote, SenderRole } from '../../helpers/enums';
import { type ThemeConfig, useTheme } from '../../theme';
import { type AssistantMessage } from '../../types';
import { type MarkdownBlock } from '../../types/blocks';
import RenderBlock from '../RenderBlock';
import { formatFullDateTime, formatRelativeTime } from '../../helpers/utils';
import { stripMarkdown } from '../../helpers/textExtraction';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import TTSButton from '../TTSButton';

interface AssistantMessageBubbleProps {
  message: AssistantMessage;
}

type CurrentVote = 'liked' | 'disliked' | 'neutral';

const OverviewMessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;

const OverviewMessage = styled.div<{ $theme: ThemeConfig; shouldBlur: boolean }>`
  background-color: ${(props) => props.$theme.colors.assistantOverviewBubbleBg};
  color: ${(props) => props.$theme.colors.assistantOverviewBubbleText};
  padding: ${(props) => props.$theme.tokens.spaceMd};
  border-radius: ${(props) => props.$theme.tokens.radiusMd};
  filter: ${(props) => (props.shouldBlur ? 'blur(4px)' : 'none')};
  transition: filter 0.3s ease;
  position: relative;
  overflow: hidden;
  font-size: 16px;
  line-height: 1.5;
  word-wrap: break-word;
  font-weight: 500;
  padding: 18px 24px;
  border-radius: ${(props) => props.$theme.tokens.radiusMd};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  /* Responsive adjustments */
  @media (max-width: 768px) {
    max-width: 85%;
    padding: 16px 20px;
    font-size: 14px;
  }
`;

const OverviewActionsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  width: 100%;
`;

const AssistantMessageBubble: React.FC<AssistantMessageBubbleProps> = ({ message }) => {
  const { voteMessage, thinkingState, messages } = useAskVantage();
  const theme = useTheme();
  const [isVoting, setIsVoting] = useState(false);
  const { demo_account: isDemoAccount } = useSelector(
    (state: RootState) => state.company.company_details
  );

  // Check if response blocks contain specific markdown modes that should hide overview
  const shouldHideOverview = () => {
    if (!message.response_blocks || message.response_blocks.length === 0) {
      return false;
    }

    const hideOverviewModes = [
      'handle_error',
      'handle_token_limit_error',
      'handle_clarification',
      'handle_out_of_scope'
    ];

    return message.response_blocks.some((block) => {
      if (block.type === 'markdown') {
        const markdownBlock = block as MarkdownBlock;
        return markdownBlock.mode && hideOverviewModes.includes(markdownBlock.mode);
      }
      return false;
    });
  };

  // Determine if this is the last assistant message after the last user message
  const isLastAssistantMessage = () => {
    // Find the last user message index
    let lastUserMessageIndex = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].message_type === SenderRole.USER) {
        lastUserMessageIndex = i;
        break;
      }
    }

    // If no user message found, don't blur
    if (lastUserMessageIndex === -1) return false;

    // Find all assistant messages after the last user message
    const assistantMessagesAfterLastUser = messages
      .slice(lastUserMessageIndex + 1)
      .filter((msg) => msg.message_type === SenderRole.ASSISTANT);

    // Check if this message is the last assistant message after the last user message
    if (assistantMessagesAfterLastUser.length === 0) return false;

    const lastAssistantMessage =
      assistantMessagesAfterLastUser[assistantMessagesAfterLastUser.length - 1];
    return lastAssistantMessage.id === message.id;
  };

  // Determine if content should be blurred (when thinking state is active AND
  //  this is the last assistant message)
  const shouldBlur = Boolean(thinkingState) && isLastAssistantMessage();

  // Initialize vote state from message property
  const currentVote =
    Boolean(message.vote) && message.vote !== MessageVote.NEUTRAL
      ? message.vote
      : ('neutral' as CurrentVote);

  const handleVote = async (vote: MessageVote) => {
    if (isVoting) return;

    setIsVoting(true);
    try {
      await voteMessage(message.id, vote);
    } catch (error) {
      console.error('Failed to vote on message:', error);
    } finally {
      setIsVoting(false);
    }
  };

  if (
    message.content &&
    (!message.response_blocks ||
      (message.response_blocks && message.response_blocks.length === 0)) &&
    !shouldBlur
  ) {
    return (
      <Wrapper $theme={theme}>
        <Bubble $theme={theme} shouldBlur={shouldBlur}>
          <Markdown remarkPlugins={[remarkGfm]}>{message.content}</Markdown>
        </Bubble>
        {message.created_at && (
          <TimestampContainer>
            <Timestamp $theme={theme} title={formatFullDateTime(message.created_at)}>
              {formatRelativeTime(message.created_at)}
            </Timestamp>
            <VotingContainer>
              {isDemoAccount && (
                <TTSButton
                  blockId={`${message.id}_content`}
                  text={stripMarkdown(message.content)}
                />
              )}
              <VoteButton
                onClick={() => handleVote(MessageVote.LIKE)}
                isActive={currentVote === 'liked'}
                disabled={isVoting}
                $theme={theme}
                voteType={MessageVote.LIKE}
              >
                {currentVote === 'liked' ? <BiSolidLike /> : <BiLike />}
              </VoteButton>
              <VoteButton
                onClick={() => handleVote(MessageVote.DISLIKE)}
                isActive={currentVote === 'disliked'}
                disabled={isVoting}
                $theme={theme}
                voteType={MessageVote.DISLIKE}
              >
                {currentVote === 'disliked' ? <BiSolidDislike /> : <BiDislike />}
              </VoteButton>
            </VotingContainer>
          </TimestampContainer>
        )}
      </Wrapper>
    );
  }

  return (
    <>
      {message.content &&
      message.response_blocks &&
      message.response_blocks.length > 0 &&
      !shouldHideOverview() ? (
        <OverviewMessageWrapper>
          <OverviewMessage $theme={theme} shouldBlur={shouldBlur}>
            <Markdown remarkPlugins={[remarkGfm]}>{message.content}</Markdown>
          </OverviewMessage>
          {isDemoAccount && (
            <OverviewActionsContainer>
              <TTSButton blockId={`${message.id}_overview`} text={stripMarkdown(message.content)} />
            </OverviewActionsContainer>
          )}
        </OverviewMessageWrapper>
      ) : null}
      <Wrapper $theme={theme}>
        {message.response_blocks && message.response_blocks.length > 0 && (
          <Bubble $theme={theme} shouldBlur={shouldBlur}>
            {message.response_blocks.map((block, index) => {
              const blockId = `${message.id}_block_${index}`;
              const chartId = block.type === 'chart' ? blockId : undefined;
              return (
                <RenderBlock
                  key={`${block.type}-${index}`}
                  block={block}
                  chartId={chartId}
                  blockId={blockId}
                />
              );
            })}
          </Bubble>
        )}
        {message.created_at && (
          <TimestampContainer>
            <Timestamp $theme={theme} title={formatFullDateTime(message.created_at)}>
              {formatRelativeTime(message.created_at)}
            </Timestamp>
            {!shouldHideOverview() && (
              <VotingContainer>
                <VoteButton
                  onClick={() => handleVote(MessageVote.LIKE)}
                  isActive={currentVote === 'liked'}
                  disabled={isVoting}
                  $theme={theme}
                  voteType={MessageVote.LIKE}
                >
                  {currentVote === 'liked' ? <BiSolidLike /> : <BiLike />}
                </VoteButton>
                <VoteButton
                  onClick={() => handleVote(MessageVote.DISLIKE)}
                  isActive={currentVote === 'disliked'}
                  disabled={isVoting}
                  $theme={theme}
                  voteType={MessageVote.DISLIKE}
                >
                  {currentVote === 'disliked' ? <BiSolidDislike /> : <BiDislike />}
                </VoteButton>
              </VotingContainer>
            )}
          </TimestampContainer>
        )}
      </Wrapper>
    </>
  );
};

export default AssistantMessageBubble;

const Wrapper = styled.div<{ $theme: ThemeConfig }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: ${(props) => props.$theme.tokens.spaceLg};
  border-radius: ${(props) => props.$theme.tokens.radiusMd || '8px'};
  padding: ${(props) => props.$theme.tokens.spaceMd || '16px'};
  opacity: 0.95;
`;

const Bubble = styled.div<{ $theme: ThemeConfig; shouldBlur: boolean }>`
  /* Background now handled by Wrapper */
  filter: ${(props) => (props.shouldBlur ? 'blur(4px)' : 'none')};
  transition: filter 0.3s ease;
  position: relative;
  overflow: hidden;
  width: 100%;

  ${(props) =>
    props.shouldBlur &&
    `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.1) 0%,
        rgba(255, 255, 255, 0.05) 25%,
        rgba(255, 255, 255, 0.15) 50%,
        rgba(255, 255, 255, 0.05) 75%,
        rgba(255, 255, 255, 0.1) 100%
      );
      backdrop-filter: blur(8px);
      border-radius: ${props.$theme.tokens.radiusMd || '8px'};
      pointer-events: none;
      z-index: 1;
      animation: shimmer 5s linear infinite;
    }
    
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 300px;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.4),
        rgba(255, 255, 255, 0.7),
        rgba(255, 255, 255, 0.4),
        transparent
      );
      pointer-events: none;
      z-index: 3;
      animation: glare 1.2s ease-in-out infinite;
      transform: skewX(-15deg);
    }
    
    @keyframes shimmer {
      0% {
        background-position: -100% 0;
      }
      100% {
        background-position: 100% 0;
      }
    }
    
    @keyframes glare {
      0% {
        left: -300px;
        opacity: 0;
      }
      15% {
        opacity: 1;
      }
      85% {
        opacity: 1;
      }
      100% {
        left: 100%;
        opacity: 0;
      }
    }
    
    > * {
      position: relative;
      z-index: 2;
    }
  `}
`;

const TimestampContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
  width: 100%;
`;

const Timestamp = styled.div<{ $theme: ThemeConfig }>`
  font-size: 10px;
  color: ${(props) => props.$theme.colors.sidebarGroupColor};
  opacity: 0.5;
`;

const VotingContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const VoteButton = styled.button<{
  $theme: ThemeConfig;
  isActive: boolean;
  disabled: boolean;
  voteType: MessageVote;
}>`
  margin-top: 10px;
  background: none;
  border: none;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: ${(props) =>
    props.isActive
      ? props.voteType === MessageVote.LIKE
        ? props.$theme.colors.voteLiked
        : props.$theme.colors.voteDisliked
      : props.$theme.colors.voteNeutral};
  opacity: ${(props) => (props.disabled ? 0.5 : props.isActive ? 1 : 0.6)};
  font-size: 16px;

  &:hover {
    opacity: ${(props) => (props.disabled ? 0.5 : 1)};
    background-color: ${(props) =>
      props.disabled
        ? 'transparent'
        : props.$theme.colors.messageBoxBorder || 'rgba(128, 128, 128, 0.1)'};
    transform: ${(props) => (props.disabled ? 'none' : 'scale(1.1)')};
  }

  &:active {
    transform: ${(props) => (props.disabled ? 'none' : 'scale(0.95)')};
  }
`;
